// Pricing migration 18 (ADO-2249): what a priced product says about its price, as pure
// functions every surface shares.
//
// - The kind (AC1): only a promotion and a contract are named. A WOW price reads as a standard
//   one (DEC-9: the WOW entry already says so), and a standard price carries no label.
// - The saving (AC2): the was price minus the unit price, per case, in whole cents, and only
//   when the quote has a was price. A promotion that beat an absolute contract has none.
// - The offer text: the designer's copy word for word (Figma set 1, the v9 proposal board), with
//   each part the quote lacks left out (Clarifications): no `Save €X` without a was price, no
//   `Ends …` without an end date. No promotion name (DEC-7).
// - What screen readers hear (AC3): DEC-15's table, so a struck-through number is never the
//   only way the was price is told.
//
// The web computes no price here: the saving is the difference of two figures the endpoint
// sent, as the basket sums its stored figures (`cartTotals.ts`).

import { toCents } from '../basket/cartTotals';
import { formatMoney, type MoneyValue } from '../shared/money';
import type { PriceEntry } from './usePriceStore';

/** A SKU the endpoint priced. */
export type PricedEntry = Extract<PriceEntry, { readonly state: 'priced' }>;

/** A priced entry with a figure `ProductPrice` will show (it hides a zero one), else `null`. */
export function shownEntry(entry: PriceEntry | undefined): PricedEntry | null {
  return entry?.state === 'priced' && entry.unitPrice.amount > 0 ? entry : null;
}

/** True when a promotion set the price: the only kind shown in red, with an offer row. */
export function isPromotion(entry: PricedEntry): boolean {
  return entry.kind === 'promotion';
}

/**
 * The per-case saving, `wasPrice − unitPrice` in whole cents, or `null` when there is none to
 * show: not a promotion (only a promotion has a saving, DEC-6), no was price, a was price in
 * another currency, or one that is not above the price (a saving of nothing or less is never
 * shown).
 */
export function promotionSaving(entry: PricedEntry): MoneyValue | null {
  const { wasPrice, unitPrice } = entry;
  if (!isPromotion(entry) || wasPrice === undefined || wasPrice.currency !== unitPrice.currency)
    return null;
  const cents = toCents(wasPrice.amount) - toCents(unitPrice.amount);
  if (!(cents > 0)) return null;
  return { amount: cents / 100, currency: unitPrice.currency };
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * A promotion's end date as day and short month, no year (`11 Nov`), or `null` when it is not
 * a real `YYYY-MM-DD` date. Read from the digits, never through a time zone, so the day shown
 * is the day sent.
 */
export function formatPromotionEndDate(isoDate: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (match === null) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1) return null;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > daysInMonth) return null;
  return `${day} ${MONTHS[month - 1]}`;
}

/** The parts of a promotion's offer text the quote supports: the saving and the end date. */
function offerParts(entry: PricedEntry): { saving: string | null; ends: string | null } {
  const saving = promotionSaving(entry);
  const endDate =
    entry.promotionEndDate === undefined ? null : formatPromotionEndDate(entry.promotionEndDate);
  return {
    saving: saving === null ? null : formatMoney(saving),
    ends: endDate === null ? null : `Ends ${endDate}`,
  };
}

/**
 * The grid card's offer row: `Save €4.86 · Ends 11 Nov`, or `Ends 11 Nov` with no was price.
 * `null` for any kind but a promotion, and for a promotion with nothing to say.
 */
export function gridOfferText(entry: PricedEntry): string | null {
  if (!isPromotion(entry)) return null;
  const { saving, ends } = offerParts(entry);
  const parts = [saving === null ? null : `Save ${saving}`, ends].filter(
    (part): part is string => part !== null,
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * The list card's offer pill and the product page's offer strip:
 * `Promotion · Save €4.86 a case · Ends 11 Nov`, or `Promotion · Ends 11 Nov` with no was
 * price. `null` for any kind but a promotion.
 */
export function listOfferText(entry: PricedEntry): string | null {
  if (!isPromotion(entry)) return null;
  const { saving, ends } = offerParts(entry);
  return ['Promotion', saving === null ? null : `Save ${saving} a case`, ends]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/** `Was €5.49, now €4.18`, with `suffix` after the price (` per case` on a cart line). */
export function spokenWasNow(wasPrice: MoneyValue, price: MoneyValue, suffix = ''): string {
  return `Was ${formatMoney(wasPrice)}, now ${formatMoney(price)}${suffix}`;
}

/**
 * What a screen reader hears for the price (DEC-15): `Promotion. Was €21.65, now €16.79`,
 * `Promotion. €16.79` with no was price, `Contract price. €8.75`, and the price alone for a
 * standard or WOW price.
 */
export function spokenPrice(entry: PricedEntry): string {
  const price = formatMoney(entry.unitPrice);
  if (isPromotion(entry)) {
    const wasPrice = promotionSaving(entry) === null ? undefined : entry.wasPrice;
    return wasPrice === undefined
      ? `Promotion. ${price}`
      : `Promotion. ${spokenWasNow(wasPrice, entry.unitPrice)}`;
  }
  if (entry.kind === 'contract') return `Contract price. ${price}`;
  return price;
}

/** What the card and the product page draw for a priced entry (ADO-2249, Task 3; DEC-15). */
export interface ExplainedPrice {
  /** The price is red. */
  readonly promotion: boolean;
  /** The `Contract price` pill shows. */
  readonly contract: boolean;
  /** The was price struck beside the price: a promotion's, above the price only. */
  readonly wasPrice: MoneyValue | undefined;
  /** What a screen reader hears instead of the figures, or `null` to read the figure itself. */
  readonly spoken: string | null;
  /** `+ €3.00 deposit`, or `null` when the deposit is zero. */
  readonly deposit: string | null;
}

export function explainedPrice(entry: PricedEntry): ExplainedPrice {
  const promotion = isPromotion(entry);
  const contract = entry.kind === 'contract';
  return {
    promotion,
    contract,
    wasPrice: promotionSaving(entry) === null ? undefined : entry.wasPrice,
    // Only a promotion or a contract says more than its figure.
    spoken: promotion || contract ? spokenPrice(entry) : null,
    deposit: entry.deposit.amount > 0 ? `+ ${formatMoney(entry.deposit)} deposit` : null,
  };
}
